import { RefObject, useEffect, useLayoutEffect, useRef, useState } from 'react'

import { BoardTheme, DASHBOARD_THEME_MESSAGE } from './boardTheme'

export const DASHBOARD_READY_MESSAGE = 'sparck:ready'
export const DASHBOARD_PORT_MESSAGE = 'sparck:port'
export const DASHBOARD_DATA_MESSAGE = 'sparck:data'

type Options = { enabled?: boolean; theme?: BoardTheme }

// The parent's half of the handshake, shared by the viewer and the render check. The first sparck:ready from
// the frame's window is answered with one end of a MessageChannel, handed to `onConnected`; every later ready
// is ignored. The window stays the same object when the frame navigates, so a page that replaced the original
// can say ready too, and it never gets a port. The frame's origin is opaque, so the post can only target '*'.
// Returns a function that stops listening and closes the port.
export const answerFirstReady = (
  frameWindowOf: () => Window | null | undefined,
  onConnected: (port: MessagePort) => void,
) => {
  let answered = false
  let port: MessagePort | null = null

  const onMessage = (event: MessageEvent) => {
    const frameWindow = frameWindowOf()
    if (!frameWindow || event.source !== frameWindow) return
    if (event.data?.type !== DASHBOARD_READY_MESSAGE || answered) return

    answered = true
    const channel = new MessageChannel()
    frameWindow.postMessage({ type: DASHBOARD_PORT_MESSAGE }, '*', [channel.port2])
    port = channel.port1
    onConnected(port)
  }

  window.addEventListener('message', onMessage)

  return () => {
    window.removeEventListener('message', onMessage)
    port?.close()
  }
}

// The frame's runtime says `sparck:ready` once its blocks have registered, and every payload, each refresh
// included, travels on the port its first ready gets. So does the viewer's theme, when one is given: as soon as
// the port opens, which covers a switch made before the frame was ready, then on every switch, and the frame
// repaints without loading again or getting its data again. One hook serves one frame: SandboxedDashboard
// mounts a new frame, and so a new hook, for each html.
export default function useDashboardBridge(
  iframeRef: RefObject<HTMLIFrameElement>,
  payload: unknown,
  { enabled = true, theme }: Options = {},
) {
  const port = useRef<MessagePort | null>(null)
  const answered = useRef(false)
  const [connected, setConnected] = useState(false)

  // A layout effect, so the listener is in place in the same task that mounts the frame, before the frame can
  // possibly say it is ready. A frame answered once is never answered again.
  useLayoutEffect(() => {
    if (!enabled || answered.current) return undefined

    const stop = answerFirstReady(
      () => iframeRef.current?.contentWindow,
      opened => {
        answered.current = true
        port.current = opened
        setConnected(true)
      },
    )

    return () => {
      stop()
      port.current = null
    }
  }, [iframeRef, enabled])

  // Before the data effect, so a frame that just connected gets its theme before its first payload.
  useEffect(() => {
    if (!enabled || !connected || theme === undefined) return

    port.current?.postMessage({ type: DASHBOARD_THEME_MESSAGE, theme })
  }, [enabled, connected, theme])

  useEffect(() => {
    if (!enabled || !connected || payload === undefined || payload === null) return

    port.current?.postMessage({ type: DASHBOARD_DATA_MESSAGE, payload })
  }, [enabled, connected, payload])

  return connected
}
